/**
 * Evidence SCHEMAS and their pure helpers — the isomorphic half of
 * `evidence.ts`.
 *
 * ── Why the file was split ──
 *
 * `evidence.ts` holds two things: the zod shapes an evidence submission has, and
 * `admitEvidence`, which ADMITS one. Only the second is server-only, and it is
 * server-only for a load-bearing reason — it resolves the producer's boundary
 * through `assertObservedProducerBoundary`, which reads the principal registry
 * (`producer-boundary.js` → `supervised-execution-enrollment.js` →
 * `execution-boundary.js`, where a boundary is MINTED with `node:crypto` and
 * `node:os`, and a container is probed with `node:child_process`).
 *
 * `protocol/client.ts` — the designated browser-safe subpath, exported as
 * `@excellent/core/verification-protocol-client` — re-exported two of those
 * schemas. An ES import is a module-level edge, so the browser inherited the
 * admission implementation and everything under it:
 *
 *   client/index.html → main.tsx → routes/tree.ts → routes/agent-verification.tsx
 *     → @excellent/ui/api/contracts/universal-verification.ts
 *     → @excellent/core/verification-protocol-client  (protocol/client.js)
 *     → protocol/evidence.js → protocol/producer-boundary.js
 *     → protocol/execution-boundary.js               ← node:crypto, node:os
 *
 * and the production client bundle COULD NOT BE BUILT — `browserOnlyGuard`
 * (`excellent/client/vite-guard.ts`) refuses a node builtin in the browser graph,
 * because Vite's own behaviour is to externalize it, warn, and exit 0 on an
 * artifact that ships a server-only module.
 *
 * So the schemas moved and the admission stayed. `evidence.ts` re-exports every
 * name below, which is what keeps ~20 existing `from "./evidence.js"` importers
 * — and the gate that reads `dist/verification/protocol/evidence.js` and asserts
 * it still contains `assertObservedProducerBoundary(` — unchanged.
 *
 * ── What may live here ──
 *
 * Shapes and pure functions of their arguments. A declaration that reads a
 * registry, a runtime or a clock belongs in `evidence.ts`. The rail that keeps
 * this honest is `pnpm check-client-bundle-isomorphic`, which resolves the real
 * client module graph and refuses ANY node builtin reachable from the client
 * entry — the relation, not `node:crypto` in particular.
 */
import { z } from "zod";
import { actorIdentitySchema, independenceAssessmentSchema, } from "./authority.js";
import { canonicalJsonSchema, canonicalSha256, canonicalize, } from "./canonicalize.js";
import { attemptBindingSchema, evidenceBindingSchema, protocolIdSchema, protocolTimestampSchema, protocolVersionSchema, sha256DigestSchema, uriSchema, } from "./identifiers.js";
import { UNIVERSAL_PROTOCOL_POLICY } from "./policy.js";
/**
 * The `provenance.method` an INDEPENDENTLY OBSERVED change manifest carries.
 *
 * Stamped by exactly one writer —
 * `excellent/server/directory-command-change-evidence.ts`, which derives the
 * changed-resource list with `git diff --name-only <origin.inputGitTree>
 * <receipt.gitTree>` inside the supervisor's bare object store, a store that is
 * never bind-mounted into the container that produced the change. Declared here
 * rather than at that call site because the EVALUATOR has to recognise it: a
 * `resource_bounds` determination may rest on an observation of the changes and
 * must refuse a producer's declaration of them. Two spellings of one method
 * would let the writer and the reader drift apart silently, and the drift would
 * present as a verdict, not as an error.
 *
 * The label is NOT a credential. Anyone assembling an evidence submission can
 * type it. What makes it mean something is the company it must keep — see
 * `independentlyObservedChangeManifest` in
 * `evaluators/universal-deterministic.ts`, which additionally requires the
 * collection to have happened at an execution boundary that is distinguishable
 * from the producer's as a UNIT OF EXECUTION rather than as a string.
 */
export const INDEPENDENT_CHANGE_OBSERVATION_METHOD = "independent-git-tree-diff";
/** A coding repository is observed from its host-admitted filesystem rather
 * than a supervised worker's bare Git store. Keep the method explicit so the
 * evaluator can distinguish this truthful snapshot observation from a worker
 * declaration without relabelling it as a Git-tree proof. */
export const INDEPENDENT_REPOSITORY_SNAPSHOT_OBSERVATION_METHOD = "independent-repository-snapshot-diff";
export const evidenceProvenanceSchema = z
    .object({
    originActorId: protocolIdSchema,
    collector: actorIdentitySchema,
    producerBoundaryId: protocolIdSchema,
    custodyBoundaryId: protocolIdSchema,
    method: protocolIdSchema,
    sourceUri: uriSchema,
    observedAt: protocolTimestampSchema,
})
    .strict();
export const evidenceArtifactSchema = z
    .object({
    id: protocolIdSchema,
    kind: protocolIdSchema,
    mediaType: z.string().trim().min(1),
    contentDigest: sha256DigestSchema,
    sizeBytes: z.number().int().nonnegative(),
    encoding: z.enum(["canonical_json", "utf8", "base64", "external"]),
    inlineContent: canonicalJsonSchema.nullable(),
    location: uriSchema.nullable(),
    provenance: evidenceProvenanceSchema,
    mutability: z.enum(["immutable", "versioned", "mutable"]),
    accessibility: z.enum(["accessible", "inaccessible", "redacted", "unknown"]),
    freshness: z.enum(["fresh", "stale", "expired", "unknown"]),
    contradictionGroup: protocolIdSchema.nullable(),
    disclosureClass: z.enum(["public", "tenant", "restricted", "secret_redacted"]),
})
    .strict()
    .superRefine((value, context) => {
    if (value.encoding === "external" && value.location === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["location"],
            message: "external evidence requires a location",
        });
    }
    if (value.encoding !== "external" && value.inlineContent === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["inlineContent"],
            message: "inline evidence requires content",
        });
    }
    if (value.disclosureClass === "secret_redacted" && value.inlineContent !== null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["inlineContent"],
            message: "secret-redacted evidence cannot include inline content",
        });
    }
    if (value.encoding !== "external" &&
        value.sizeBytes > UNIVERSAL_PROTOCOL_POLICY.evidenceStorage.inlineMaximumBytes) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["sizeBytes"],
            message: "large evidence must use a content-addressed external location",
        });
    }
});
export const evidenceDerivationSchema = z
    .object({
    parentArtifactId: protocolIdSchema,
    childArtifactId: protocolIdSchema,
    transformRef: protocolIdSchema,
    transformDigest: sha256DigestSchema,
})
    .strict();
export const evidenceGapSchema = z
    .object({
    id: protocolIdSchema,
    obligationId: protocolIdSchema,
    kind: z.enum(["missing", "inaccessible", "stale", "contradictory", "blocked"]),
    reasonCode: protocolIdSchema,
    detail: z.string().trim().min(1),
})
    .strict();
export const evidenceReplaySchema = z
    .object({
    command: z.string().trim().min(1),
    environmentDigest: sha256DigestSchema,
    expectedArtifactIds: z.array(protocolIdSchema),
    timeoutSeconds: z.number().int().positive(),
})
    .strict();
/**
 * What the SUBMITTER claims about its own work. Deliberately excludes
 * `MISMATCH`: that status means an independent record contradicted the claim,
 * and a party cannot supply the independent record about itself. A submitter
 * with nothing to show says FAIL, UNKNOWN or NOT_RUN.
 */
export const workerFinalClaimSchema = z
    .object({
    statement: z.string().trim().min(1),
    assertedStatus: z.enum(["PASS", "FAIL", "UNKNOWN", "NOT_RUN"]).nullable(),
    actor: actorIdentitySchema,
    assertedAt: protocolTimestampSchema,
})
    .strict();
export const evidenceSubmissionSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    binding: attemptBindingSchema,
    outputSnapshotDigest: sha256DigestSchema,
    submitter: actorIdentitySchema,
    submittedAt: protocolTimestampSchema,
    artifacts: z.array(evidenceArtifactSchema),
    derivations: z.array(evidenceDerivationSchema),
    replay: z.array(evidenceReplaySchema),
    gaps: z.array(evidenceGapSchema),
    finalClaim: workerFinalClaimSchema.nullable(),
    redactionManifestDigest: sha256DigestSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.submitter.tenantId !== value.binding.tenantId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["submitter", "tenantId"],
            message: "submitter tenant must match evidence binding",
        });
    }
    const artifactIds = value.artifacts.map((artifact) => artifact.id);
    if (new Set(artifactIds).size !== artifactIds.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["artifacts"],
            message: "artifact IDs must be unique",
        });
    }
    const artifactIdSet = new Set(artifactIds);
    for (const derivation of value.derivations) {
        if (!artifactIdSet.has(derivation.parentArtifactId) ||
            !artifactIdSet.has(derivation.childArtifactId)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["derivations"],
                message: "derivations must reference artifacts in this submission",
            });
        }
    }
});
export const admittedArtifactSchema = z
    .object({
    artifact: evidenceArtifactSchema,
    bytesVerified: z.boolean(),
    independence: independenceAssessmentSchema,
    admissionReasonCodes: z.array(protocolIdSchema),
})
    .strict();
export const admittedEvidenceBundleSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    binding: evidenceBindingSchema,
    submissionDigest: sha256DigestSchema,
    submission: evidenceSubmissionSchema,
    artifacts: z.array(admittedArtifactSchema),
    admittedBy: actorIdentitySchema,
    admittedAt: protocolTimestampSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.binding.evidenceGraphDigest !== value.submissionDigest) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["binding", "evidenceGraphDigest"],
            message: "evidence graph digest must equal the canonical submission digest",
        });
    }
    if (value.artifacts.length !== value.submission.artifacts.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["artifacts"],
            message: "every submitted artifact requires an admission result",
        });
    }
});
function artifactBytes(artifact) {
    if (artifact.encoding === "external" || artifact.inlineContent === null)
        return null;
    if (artifact.encoding === "canonical_json") {
        return canonicalize(artifact.inlineContent);
    }
    if (typeof artifact.inlineContent !== "string")
        return null;
    return artifact.inlineContent;
}
export function verifyArtifactBytes(artifact, sha256) {
    const bytes = artifactBytes(evidenceArtifactSchema.parse(artifact));
    return bytes !== null &&
        new TextEncoder().encode(bytes).byteLength === artifact.sizeBytes &&
        sha256(bytes) === artifact.contentDigest;
}
export function evidenceSubmissionDigest(submission, sha256) {
    return canonicalSha256(evidenceSubmissionSchema.parse(submission), sha256);
}
export function evidenceBindingFor(input) {
    return evidenceBindingSchema.parse({
        ...input.submission.binding,
        outputSnapshotDigest: input.submission.outputSnapshotDigest,
        evidenceGraphDigest: input.evidenceGraphDigest,
    });
}
